import { ModuleLog, getModuleLog } from "./Logger";

const Version = 1;

type Cached<T> = {
    obj: T;
    createdTime: number;
};

export function makeCached<T>(t: T): Cached<T> {
    return {
        obj: t,
        createdTime: Date.now(),
    };
}

export class StorageDb<T> {
    public ready: Promise<void>;
    readonly name: string;
    readonly cacheExpiry: number;
    private _db: any = undefined;
    private _log: ModuleLog;

    constructor(name: string, cacheExpiry = 1000 * 60 * 60 * 24 * 3) {
        this.name = name;
        this._log = getModuleLog(`${name}-storage-db`);
        this.cacheExpiry = cacheExpiry;
        this.ready = this._init();
    }

    private _init(): Promise<void> {
        return new Promise((resolve, reject) => {
            const request = window.indexedDB.open(this.name, Version);

            request.onupgradeneeded = (e: any) => {
                this._db = e.target.result;
                this._db.createObjectStore(this.name);
            };

            request.onsuccess = (e: any) => {
                this._db = e.target.result;
                resolve();
            };

            request.onerror = (e: any) => {
                this._db = e.target.result;
                this._log.error("{0}", e);
                reject(e);
            };
        });
    }

    async get(key: string): Promise<T> {
        await this.ready;
        return new Promise((resolve, reject) => {
            if (!this._db) reject();
            const request = this._getStore().get(key);
            request.onsuccess = (e: any) => {
                if (e.target.result) {
                    const cached = e.target.result as Cached<T>;
                    if (
                        this.cacheExpiry !== 0 &&
                        Date.now() - cached.createdTime > this.cacheExpiry
                    ) {
                        // expired so reject
                        reject(cached.obj);
                    } else {
                        resolve(cached.obj);
                    }
                } else {
                    reject();
                }
            };
            request.onerror = reject;
        });
    }

    async allKeys(): Promise<Array<string>> {
        await this.ready;
        return new Promise<Array<string>>((resolve, reject) => {
            const request = this._getStore().getAllKeys();
            request.onsuccess = (e: any) => {
                if (e.target.result) {
                    resolve(e.target.result);
                } else {
                    reject();
                }
            };
        });
    }

    async all(): Promise<Array<T>> {
        await this.ready;
        return new Promise<Array<T>>((resolve, reject) => {
            const request = this._getStore().getAll();
            request.onsuccess = (e: any) => {
                if (e.target.result) {
                    resolve(e.target.result.map((x: Cached<T>) => x.obj));
                } else {
                    reject();
                }
            };
        });
    }

    private _getStore() {
        return this._db.transaction(this.name, "readwrite").objectStore(this.name);
    }

    async delete(key: string) {
        await this.ready;
        if (!this._db) {
            return;
        }
        return new Promise<void>((resolve, reject) => {
            const request = this._getStore().delete(key);
            request.onsuccess = (_e: any) => {
                resolve();
            };
            request.onerror = reject;
        });
    }

    async set(key: string, value: T): Promise<void> {
        await this.ready;
        return new Promise((resolve, reject) => {
            if (!this._db) reject();
            const request = this._getStore().put(makeCached(value), key);
            request.onsuccess = resolve;
            request.onerror = (error: any) => {
                this._log.error("set failed - {0}", error);
                reject();
            };
        });
    }

    async setMultiple(items: { key: string; value: T }[]): Promise<void> {
        await this.ready;
        return new Promise((resolve, reject) => {
            if (!this._db) reject();
            const tx = this._db.transaction(this.name, "readwrite");
            tx.oncomplete = function () {
                resolve();
            };
            tx.onerror = function (error: any) {
                this._log.error("setMultiple failed - {0}", error);
                reject();
            };
            const store = tx.objectStore(this.name);
            for (let i = 0; i < items.length; i++) {
                const item = items[i];
                store.put(makeCached(item.value), item.key);
            }
        });
    }

    async deleteDb() {
        await this.ready;
        if (this._db) {
            this._db.close();
            this._db = undefined;
        }
        window.indexedDB.deleteDatabase(this.name);
        this.ready = this._init();
    }
}
